import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import querys from 'services/querys';
import { filterPartiesByRole, filterPartiesByName, filterPartiesByOthers } from '../services';
import { uniqueParties } from '../utils';
import useAlert from 'utils/alert';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'utils/helpers';
import { CACHE_30_DAYS, LISTING_CODE_ALLOWED_ORDER_ASSOCIATIONS } from 'utils/constants';
import { getListing } from 'graphql/listings/queries';
import { NewMemberOfType, NewOrderPartyType } from 'types/order';
import { useInfoUpdateEvent } from 'hooks/useInfoUpdateEvent';
import { useUserInformationContext } from 'contexts/userContext';
import { useAccountingUpdateSocketEvent } from 'hooks/useAccountingUpdateFromSocket';
import { infoUpdateType } from 'types/notifications';
import { useSelectedPartyId } from './useSelectedPartyId';

export type PartyItemList = {
  id: string;
  name: string;
  member_of?: NewMemberOfType[];
  kinds?: string[];
};

export type PartyList = {
  kind: string;
  parties: PartyItemList[];
};

const useParties = () => {
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { id: orderId } = useParams<{ id: string }>();
  const selectedPartyId = useSelectedPartyId();
  const [partiesList, setPartiesList] = useState<NewOrderPartyType[]>([]);
  const [partiesListRole, setPartiesListRole] = useState<PartyList[]>([]);
  const [partiesListName, setPartiesListName] = useState<PartyItemList[]>([]);
  const [partiesListOthers, setPartiesListOthers] = useState<PartyList[]>([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const { currentUser } = useUserInformationContext();
  const showAlert = useAlert();
  const queryCache = useQueryClient();

  useAccountingUpdateSocketEvent({
    callback: () => {
      queryCache.invalidateQueries(['party-order', orderId]);
      queryCache.invalidateQueries(['order-parties', orderId]);
    }
  });

  const handleFilterParties = (parties: NewOrderPartyType[]) => {
    const uniquePartyList = uniqueParties(parties);

    setPartiesListRole(filterPartiesByRole(uniquePartyList));
    setPartiesListName(filterPartiesByName(uniquePartyList));
    setPartiesListOthers(filterPartiesByOthers(uniquePartyList));
    setPartiesList(uniquePartyList);
  };

  const partiesQuery = useQuery(['order-parties', orderId], () => querys.getOnlyOrderParties(orderId), {
    enabled: !!orderId,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.getOrder) {
        const { parties } = data.getOrder;
        handleFilterParties(parties);
      }
    },
  });

  const { mutate: createAssociation, ...responseCreateAssociation } = useMutation(
    querys.createAssociation,
    {
      onSuccess: () => {
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        queryCache.invalidateQueries(['party-order', orderId]);
        queryCache.invalidateQueries(['order-parties', orderId]);
        responseCreateAssociation.reset();
      },
    }
  );

  const handleCreateAssociation = (
    partyId: string,
    kind: string,
    callback: () => void
  ) => {
    if (responseCreateAssociation.isLoading) return;

    createAssociation(
      {
        id: orderId!,
        partyId,
        kind,
        linkedToId: '',
        linkedToKind: '',
      },
      {
        onSuccess: () => callback(),
      }
    );
  };

  const { mutate: deleteAssociation, ...responseDeleteAssociation } = useMutation(
    querys.deleteAssociation,
    {
      onSuccess: (data) => {
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        const parties = data.deleteOrderAssociation?.parties || [];
        handleFilterParties(parties);

        responseDeleteAssociation.reset();
      },
    }
  );

  const handleDeleteAssociation = (
    partyId: string,
    kind: string,
    onSuccess?: () => void
  ) => {
    deleteAssociation(
      { id: orderId!, partyId, kind },
      {
        onSuccess: () => {
          if (selectedPartyId === partyId) {
            history.push(url);
          }
          onSuccess?.();
        },
      }
    );
  };

  const { data: listingResponse } = useQuery(
    ['getListing', LISTING_CODE_ALLOWED_ORDER_ASSOCIATIONS],
    {
      queryFn: () => getListing({ code: LISTING_CODE_ALLOWED_ORDER_ASSOCIATIONS }),
      refetchOnWindowFocus: false,
      staleTime: CACHE_30_DAYS,
    }
  );

  const listingEntries = listingResponse?.getListing.entries || [];

  const handleViewChange = (id: string) => {
    history.push(`${url.endsWith('/') ? url : url.concat('/')}${id}`);
  };
  useInfoUpdateEvent((data: infoUpdateType) => {
    if ((data.kind === "createAssociation" || data.kind === "deleteAssociacion" || data.kind === 'workerAccountingLedger') && currentUser?.emails.some(({ address }) => address === data.email)) {
      queryCache.invalidateQueries(['order-parties', orderId]);
    }
  });

  return {
    partiesListRole,
    partiesListName,
    partiesListOthers,
    openAddModal,
    setOpenAddModal,
    handleCreateAssociation,
    handleDeleteAssociation,
    listingEntries,
    path,
    url,
    t,
    handleViewChange,
    partiesList,
    handleFilterParties,
    selectedPartyId,
    isLoading: partiesQuery.isLoading,
    isError: partiesQuery.isError,
    refetch: partiesQuery.refetch,
    isAdding: responseCreateAssociation.isLoading,
    isDeleting: responseDeleteAssociation.isLoading,
  };
};

export default useParties;
